import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { WHATSAPP_URL, PHONE_DISPLAY, PHONE_E164 } from '../config/contact';
import './ContactPage.css';

const BUSINESS_TYPES = [
  'E-Commerce / Online Store',
  'Healthcare & Clinics',
  'Real Estate',
  'Education & Coaching',
  'Travel & Hospitality',
  'Marketing Agency',
  'SaaS / Tech',
  'Other',
];

const WHAT_NEXT = [
  {
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.4 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 1.1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 5.55 5.55l1.76-1.76a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
    title: 'We\'ll call you within 24h',
    desc: 'A Zepliq specialist will reach out to understand your business.',
  },
  {
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    title: 'Free setup session',
    desc: 'We\'ll set up your bot, train the AI, and connect your channels together.',
  },
  {
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    title: 'Go live the same day',
    desc: 'Your AI bot goes live and starts handling conversations immediately.',
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '', email: '', whatsapp: '', business: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, particles = [];
    const PARTICLE_COUNT = 55;
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * W; this.y = Math.random() * H;
        this.r = Math.random() * 1.8 + 0.4;
        this.dx = (Math.random() - 0.5) * 0.25; this.dy = (Math.random() - 0.5) * 0.25;
        this.o = Math.random() * 0.5 + 0.15;
        this.hue = Math.random() > 0.6 ? 245 : 270;
      }
      update() { this.x += this.dx; this.y += this.dy; if (this.x < 0 || this.x > W || this.y < 0 || this.y > H) this.reset(); }
      draw() { ctx.beginPath(); ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2); ctx.fillStyle = `hsla(${this.hue}, 80%, 55%, ${this.o})`; ctx.fill(); }
    }
    for (let i = 0; i < PARTICLE_COUNT; i++) particles.push(new Particle());
    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y;
        const d = Math.sqrt(dx*dx + dy*dy);
        if (d < 100) { ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y); ctx.strokeStyle = `rgba(99,102,241,${0.06*(1-d/100)})`; ctx.lineWidth = 0.5; ctx.stroke(); }
      }
    };
    const animate = () => { ctx.clearRect(0,0,W,H); particles.forEach(p=>{p.update();p.draw();}); drawLines(); animRef.current = requestAnimationFrame(animate); };
    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animRef.current); };
  }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = e => {
    e.preventDefault();
    const { name, email, business } = form;
    if (!name.trim() || !email.trim() || !business) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1200);
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us — Talk to a WhatsApp Automation Expert"
        description="Contact Zepliq for help setting up your WhatsApp automation, pricing questions, or enterprise enquiries. Our team responds within 24 hours. Book a free demo today."
        keywords="contact Zepliq, WhatsApp automation support, Zepliq demo, WhatsApp chatbot help, contact WhatsApp bot company"
        canonical="/contact"
      />

      {/* ── HERO ── */}
      <section className="contact-hero">
        <canvas ref={canvasRef} className="contact-hero__canvas" aria-hidden="true" />
        <div className="contact-hero__blob contact-hero__blob--1" aria-hidden="true" />
        <div className="contact-hero__blob contact-hero__blob--2" aria-hidden="true" />
        <div className="container contact-hero__inner">
          <span className="badge badge-brand">Get Started Free</span>
          <h1 className="display-xl contact-hero__title">
            Let's automate your<br />
            <span className="gradient-text">business together.</span>
          </h1>
          <p className="text-lg contact-hero__sub">
            Tell us about your business and we'll set up Zepliq for you, free.
            Most businesses go live the same day.
          </p>
        </div>
      </section>

      {/* ── MAIN ── */}
      <section className="contact-main section-sm">
        <div className="container">
          <div className="contact-grid">

            {/* ── FORM ── */}
            <div className="contact-form-wrap">
              {submitted ? (
                <div className="contact-success">
                  <div className="contact-success__icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h2 className="contact-success__title">Message received!</h2>
                  <p className="contact-success__sub">
                    We'll be in touch within 24 hours. Check your email, we'll send a confirmation shortly.
                  </p>
                  <Link to="/" className="btn btn-primary">Back to Home</Link>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <div className="contact-form__header">
                    <h2 className="contact-form__title">Start your free setup</h2>
                    <p className="contact-form__sub">No credit card. No commitment. Live in 5 minutes.</p>
                  </div>

                  <div className="contact-form__row">
                    <div className="contact-form__field">
                      <label className="contact-form__label" htmlFor="name">Full Name</label>
                      <input
                        id="name" name="name" type="text"
                        className="contact-form__input"
                        placeholder="John Smith"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="contact-form__field">
                      <label className="contact-form__label" htmlFor="email">Email Address</label>
                      <input
                        id="email" name="email" type="email"
                        className="contact-form__input"
                        placeholder="john@company.com"
                        value={form.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="contact-form__row">
                    <div className="contact-form__field">
                      <label className="contact-form__label" htmlFor="whatsapp">WhatsApp Number</label>
                      <input
                        id="whatsapp" name="whatsapp" type="tel"
                        className="contact-form__input"
                        placeholder="+1 234 567 8900"
                        value={form.whatsapp}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="contact-form__field">
                      <label className="contact-form__label" htmlFor="business">Business Type</label>
                      <select
                        id="business" name="business"
                        className="contact-form__input contact-form__select"
                        value={form.business}
                        onChange={handleChange}
                        required
                      >
                        <option value="" disabled>Select your industry</option>
                        {BUSINESS_TYPES.map(b => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="contact-form__field">
                    <label className="contact-form__label" htmlFor="message">
                      Tell us about your business
                      <span className="contact-form__optional">optional</span>
                    </label>
                    <textarea
                      id="message" name="message"
                      className="contact-form__input contact-form__textarea"
                      placeholder="What channels do you want to automate? Any specific goals?"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg contact-form__submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="contact-form__spinner" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Get Started Free
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      </>
                    )}
                  </button>

                  <p className="contact-form__privacy">
                    By submitting, you agree to our{' '}
                    <Link to="/contact" className="contact-form__link">Privacy Policy</Link>.
                    We never share your data.
                  </p>

                  <div className="contact-form__stats">
                    <div className="contact-form__stat">
                      <span className="contact-form__stat-value">500+</span>
                      <span className="contact-form__stat-label">Businesses live</span>
                    </div>
                    <div className="contact-form__stat-divider" />
                    <div className="contact-form__stat">
                      <span className="contact-form__stat-value">4.9 ★</span>
                      <span className="contact-form__stat-label">Average rating</span>
                    </div>
                    <div className="contact-form__stat-divider" />
                    <div className="contact-form__stat">
                      <span className="contact-form__stat-value">&lt; 24h</span>
                      <span className="contact-form__stat-label">Go live time</span>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* ── SIDEBAR ── */}
            <div className="contact-sidebar">

              {/* What happens next */}
              <div className="contact-sidebar__card">
                <h3 className="contact-sidebar__heading">What happens next?</h3>
                <div className="contact-sidebar__steps">
                  {WHAT_NEXT.map((s, i) => (
                    <div key={i} className="contact-sidebar__step">
                      <div className="contact-sidebar__step-icon">{s.icon}</div>
                      <div className="contact-sidebar__step-body">
                        <p className="contact-sidebar__step-title">{s.title}</p>
                        <p className="contact-sidebar__step-desc">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact info */}
              <div className="contact-sidebar__card">
                <h3 className="contact-sidebar__heading">Or reach us directly</h3>
                <div className="contact-sidebar__info">
                  <a href="mailto:support@zepliq.com" className="contact-sidebar__info-row">
                    <span className="contact-sidebar__info-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </span>
                    <div>
                      <span className="contact-sidebar__info-label">Email</span>
                      <span className="contact-sidebar__info-value">support@zepliq.com</span>
                    </div>
                  </a>
                  <a href={WHATSAPP_URL} className="contact-sidebar__info-row" target="_blank" rel="noopener noreferrer">
                    <span className="contact-sidebar__info-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </span>
                    <div>
                      <span className="contact-sidebar__info-label">WhatsApp</span>
                      <span className="contact-sidebar__info-value">Chat with us</span>
                    </div>
                  </a>
                  <a href={`tel:${PHONE_E164}`} className="contact-sidebar__info-row">
                    <span className="contact-sidebar__info-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    </span>
                    <div>
                      <span className="contact-sidebar__info-label">Phone</span>
                      <span className="contact-sidebar__info-value">{PHONE_DISPLAY}</span>
                    </div>
                  </a>
                  <div className="contact-sidebar__info-row contact-sidebar__info-row--plain">
                    <span className="contact-sidebar__info-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </span>
                    <div>
                      <span className="contact-sidebar__info-label">Response Time</span>
                      <span className="contact-sidebar__info-value">Under 24 hours</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust note */}
              <div className="contact-sidebar__trust">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                Your data is encrypted and never shared with third parties.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
